// Driving licence OCR service for account address verification.
//
// Replaces the BENEFIT/Wathiq eKYC step ("confirm US citizenship") that ran at
// the end of registration and on first sign-in. The user uploads their driving
// licence, Vista OCR extracts their address, the user confirms/corrects it in a
// form, and the confirmed values are saved via the account API — which is what
// marks them verified. See addressVerificationSlice.js for the save call.
//
// On a Bahraini driving licence the licence number is the holder's CPR, so it
// doubles as the identity check: the extracted number must match the CPR the
// user signed in with, otherwise they are uploading somebody else's licence.

import {
  validateDocument,
  VISTA_CASE_TYPES,
  VISTA_DOCUMENT_TYPES,
} from './vistaOcrService';

// Keys as returned by the licence read — today `output.data` of the /cases
// response, e.g. { flat, BLD, road, block, area, licenseNo, name, ... }.
// Aliases are tried left→right and matched case-insensitively, so the models
// can rename a field without breaking this.
const OCR_FIELD_ALIASES = {
  flatNumber: ['flat', 'flatNumber', 'flatNo', 'apartmentNumber'],
  buildingNumber: ['BLD', 'building', 'buildingNumber', 'buildingNo'],
  roadNumber: ['road', 'roadNumber', 'roadNo', 'street'],
  blockNumber: ['block', 'blockNumber', 'blockNo'],
  area: ['area', 'areaEnglish', 'areaName', 'city'],
};

// The licence number is the CPR; the rest is shown or logged for context only.
const OCR_IDENTITY_ALIASES = {
  licenseNumber: ['licenseNo', 'licenceNo', 'licenseNumber', 'licenceNumber'],
  fullName: ['name', 'fullName', 'nameEnglish', 'fullname'],
  nationality: ['nationality', 'nationalityEnglish'],
  birthDate: ['birthDate', 'dateOfBirth', 'dob'],
  gender: ['gender', 'sex'],
};

/**
 * Reduce an identity number to digits so formatting differences never cause a
 * false mismatch.
 * @param {string} value
 * @returns {string}
 */
const normaliseIdNumber = (value) => `${value ?? ''}`.replace(/\D/g, '');

/**
 * Pull the first present alias out of an extracted-data object.
 * @param {Object} data - extraction.extractedData
 * @param {string[]} aliases - candidate keys, most-specific first
 * @returns {string} The trimmed value, or "" when no alias holds a real value
 */
const pickField = (data, aliases) => {
  if (!data || typeof data !== 'object') return '';

  const lowerCaseIndex = Object.keys(data).reduce((acc, key) => {
    acc[key.toLowerCase()] = data[key];
    return acc;
  }, {});

  for (const alias of aliases) {
    const value = lowerCaseIndex[alias.toLowerCase()];
    if (value === undefined || value === null) continue;

    const text = `${value}`.trim();
    // Guard against an unsubstituted template placeholder such as
    // "<extracted_flat>" leaking through as if it were real data.
    if (!text || /^<.*>$/.test(text)) continue;

    return text;
  }

  return '';
};

/**
 * Find the driving-licence document in a Vista case response.
 * The response carries a `documents` array; prefer the licence entry but fall
 * back to the only document present when the label differs.
 * @param {Object} ocrData - the case object returned by Vista
 * @returns {Object|null}
 */
const findLicenceDocument = (ocrData) => {
  const documents = ocrData?.documents;
  if (!Array.isArray(documents) || documents.length === 0) return null;

  const label = VISTA_DOCUMENT_TYPES.DRIVING_LICENSE.LICENSE.toLowerCase();
  const matched = documents.find(
    (doc) => `${doc?.documentType ?? ''}`.trim().toLowerCase() === label
  );

  return matched || documents[0];
};

/**
 * Run driving licence OCR in validate-only mode.
 * Nothing is persisted to the Vista dashboard — this is a read to pre-fill the
 * form. As everywhere else in the app, OCR failure is non-blocking: the caller
 * falls back to manual entry.
 * @param {File} file - Driving licence image or PDF
 * @returns {Promise<Object>} { success, data, rawResponse } | { success: false, error }
 */
export const processDrivingLicense = async (file) => {
  try {
    return await validateDocument(
      file,
      VISTA_DOCUMENT_TYPES.DRIVING_LICENSE.LICENSE,
      VISTA_CASE_TYPES.DRIVING_LICENSE
    );
  } catch (error) {
    console.error('[Vista] Driving licence processing error:', error);
    return {
      success: false,
      error: {
        type: 'PROCESSING_ERROR',
        message: 'Failed to process driving licence',
        details: error.message,
      },
    };
  }
};

/**
 * The extracted licence fields, from whichever response shape came back.
 *
 * /cases answers `{ output: { status, data: { flat, BLD, road, block, area,
 * licenseNo, ... } } }`. The older case shape carried them at
 * `documents[].extraction.extractedData`; it is still read so a rollback on
 * the Vista side does not silently empty the form. `status: false` means the
 * read failed, so nothing from it is trusted.
 *
 * @param {Object} ocrData - result.data from processDrivingLicense
 * @returns {{ data: Object, confidence: number|null }|null}
 */
const extractLicenceFields = (ocrData) => {
  const output = ocrData?.output ?? ocrData;
  const data = output?.data;
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    if (output.status === false) return null;
    return { data, confidence: output.confidence ?? null };
  }

  const document = findLicenceDocument(ocrData);
  if (!document) return null;
  // A document still mid-pipeline has no trustworthy extraction yet.
  if (document.processingStatus && document.processingStatus !== 'completed') {
    return null;
  }
  if (!document.extraction?.extractedData) return null;
  return {
    data: document.extraction.extractedData,
    confidence: document.extraction.confidence ?? null,
  };
};

// "ISA TOWN" -> "Isa Town". Latin only: Arabic has no case. The page prefers
// its own block -> area lookup, so this only shows when the block is not in
// that list.
const toTitleCase = (text) =>
  /[a-z]/i.test(text)
    ? text
        .toLowerCase()
        .replace(/(^|[\s-])([a-z])/g, (m, sep, ch) => sep + ch.toUpperCase())
    : text;

/**
 * Transform a Vista driving licence case response into the address form shape.
 * Returns every field as a string so the values drop straight into controlled
 * inputs; missing fields come back as "" and are left for the user.
 * @param {Object} ocrData - result.data from processDrivingLicense
 * @returns {Object|null} Extracted fields, or null when nothing usable came back
 */
export const transformDrivingLicenseData = (ocrData) => {
  const extracted = extractLicenceFields(ocrData);
  if (!extracted) return null;
  const { data, confidence } = extracted;

  return {
    flatNumber: pickField(data, OCR_FIELD_ALIASES.flatNumber),
    buildingNumber: pickField(data, OCR_FIELD_ALIASES.buildingNumber),
    roadNumber: pickField(data, OCR_FIELD_ALIASES.roadNumber),
    blockNumber: pickField(data, OCR_FIELD_ALIASES.blockNumber),
    area: toTitleCase(pickField(data, OCR_FIELD_ALIASES.area)),
    licenseNumber: pickField(data, OCR_IDENTITY_ALIASES.licenseNumber),
    fullName: pickField(data, OCR_IDENTITY_ALIASES.fullName),
    nationality: pickField(data, OCR_IDENTITY_ALIASES.nationality),
    birthDate: pickField(data, OCR_IDENTITY_ALIASES.birthDate),
    gender: pickField(data, OCR_IDENTITY_ALIASES.gender),
    confidence,
    rawData: data,
  };
};

/**
 * Check the licence belongs to the signed-in user.
 * The licence number on a Bahraini licence is the holder's CPR.
 *
 * Returns an explicit verdict rather than a boolean so the caller can tell
 * "this is someone else's licence" (block) apart from "no number was read"
 * (fall back to manual entry, as OCR failure does everywhere else).
 *
 * @param {Object} transformed - output of transformDrivingLicenseData
 * @param {string} userCpr - CPR the user authenticated with
 * @returns {{ verdict: 'match'|'mismatch'|'unknown', licenseNumber: string }}
 */
export const checkLicenceBelongsToUser = (transformed, userCpr) => {
  const licenseNumber = transformed?.licenseNumber || '';
  const extracted = normaliseIdNumber(licenseNumber);
  const expected = normaliseIdNumber(userCpr);

  if (!extracted || !expected) {
    return { verdict: 'unknown', licenseNumber };
  }

  return {
    verdict: extracted === expected ? 'match' : 'mismatch',
    licenseNumber,
  };
};

/**
 * True when OCR gave us at least one address field worth pre-filling.
 * @param {Object} transformed - output of transformDrivingLicenseData
 * @returns {boolean}
 */
export const hasAnyAddressField = (transformed) =>
  Boolean(
    transformed &&
      Object.keys(OCR_FIELD_ALIASES).some((field) => transformed[field])
  );
